// The asset manifest the server builds from assets/ (art-assets §2), and name-skin matching
// (ui-flow §4.2). M1 runs without art: a missing or broken manifest is the empty manifest.
//
// Implementer: client render/assets. Consumers: assets/loader.ts, ui (skin picker, name skins),
// app.ts (boot fetch; dev polling every 3 s in M2). The JSON shape is the wire contract with the
// server's assets/manifest.ts (to be moved into @ballio/shared).
//
// Everything from the server is untrusted: `parseManifest` keeps only well-formed entries (file
// names per art-assets §1, ids, 8-hex hashes, `#rrggbb` colours) and never throws.

import { serverUrl } from '../net/server-url.js';

export interface ManifestFile {
  readonly bytes: number;
  readonly w: number;
  readonly h: number;
  /** First 8 hex digits of the SHA-1: cache-busting `?v=<hash>`. */
  readonly hash: string;
  /**
   * `false` when `assets/world/world.json` turns multiply tinting off for this file (art-assets
   * §5); absent = the slot's default.
   */
  readonly tint?: boolean;
}

export interface ManifestSkin {
  readonly id: string;
  readonly name: string;
  /** Path under /assets/, e.g. `skins/fox.png`. */
  readonly file: string;
  readonly hash: string;
  /** Lower-case nicks that apply the skin when the picker is on "None". */
  readonly keywords: readonly string[];
  /** Outline colour `#rrggbb`; absent = darkened cell colour. */
  readonly border?: string;
  readonly order: number;
  /** Keyword-only skin (not shown in the picker). */
  readonly hidden?: boolean;
}

/** theme.json overrides (art-assets §2); every key optional. Colours are `#rrggbb`. */
export interface ManifestTheme {
  readonly palette?: readonly string[];
  readonly virus?: string;
  readonly spawner?: string;
  readonly teams?: readonly string[];
  readonly plain?: string;
  readonly ink?: string;
  readonly paperLight?: string;
  readonly paperDark?: string;
  readonly gridLight?: string;
  readonly gridDark?: string;
}

export interface AssetManifest {
  readonly version: 1;
  /** Keyed by path under assets/, e.g. `world/virus.png`. */
  readonly files: Readonly<Record<string, ManifestFile>>;
  readonly skins: readonly ManifestSkin[];
  readonly theme?: ManifestTheme;
}

export const EMPTY_MANIFEST: AssetManifest = Object.freeze({
  version: 1,
  files: Object.freeze({}),
  skins: Object.freeze([]) as readonly ManifestSkin[],
});

/** File paths under assets/ (art-assets §1): lowercase a–z, 0–9 and `-`, PNG or WebP. */
export const ASSET_PATH_RE = /^[a-z0-9-]+(?:\/[a-z0-9-]+)*\.(?:png|webp)$/;
/** `files` keys: the image paths above plus drop-in sounds (art-assets §8: `sounds/*.ogg|mp3`). */
export const MANIFEST_FILE_RE = /^[a-z0-9-]+(?:\/[a-z0-9-]+)*\.(?:png|webp|ogg|mp3)$/;
/** Preset skin ids (art-assets §4.1). */
export const SKIN_ID_RE = /^[a-z0-9-]{1,32}$/;
const HASH_RE = /^[0-9a-f]{4,64}$/i;
const HEX_COLOR_RE = /^#?(?:[0-9a-f]{6}|[0-9a-f]{3})$/i;

/** Palette size limits for theme.json (art-assets §2: 8 to 32 colours). */
export const THEME_PALETTE_MIN = 8;
export const THEME_PALETTE_MAX = 32;

const MAX_SKINS = 4096;
const MAX_FILES = 8192;
const MAX_KEYWORDS = 32;
const MAX_NAME_CHARS = 64;

type Json = Record<string, unknown>;

function isObject(v: unknown): v is Json {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function isCount(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 && Math.floor(v) === v;
}

/** `#rrggbb` (lower case) for a valid hex colour, else null. */
export function normalizeHexColor(v: unknown): string | null {
  if (typeof v !== 'string' || !HEX_COLOR_RE.test(v)) return null;
  let h = v.startsWith('#') ? v.slice(1) : v;
  if (h.length === 3) h = h.replace(/./g, (c) => c + c);
  return `#${h.toLowerCase()}`;
}

function parseFile(raw: unknown): ManifestFile | null {
  if (!isObject(raw)) return null;
  const { bytes, w, h, hash, tint } = raw;
  if (!isCount(bytes) || !isCount(w) || !isCount(h)) return null;
  if (typeof hash !== 'string' || !HASH_RE.test(hash)) return null;
  const file: { -readonly [K in keyof ManifestFile]: ManifestFile[K] } = {
    bytes,
    w,
    h,
    hash: hash.toLowerCase(),
  };
  if (typeof tint === 'boolean') file.tint = tint;
  return Object.freeze(file);
}

/** The default skin name: the id with its first letter capitalised and hyphens as spaces. */
export function defaultSkinName(id: string): string {
  const s = id.replace(/-/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function parseSkin(raw: unknown): ManifestSkin | null {
  if (!isObject(raw)) return null;
  const { id, name, file, hash, keywords, border, order, hidden } = raw;
  if (typeof id !== 'string' || !SKIN_ID_RE.test(id)) return null;
  if (typeof file !== 'string' || !ASSET_PATH_RE.test(file)) return null;
  if (typeof hash !== 'string' || !HASH_RE.test(hash)) return null;
  const kw: string[] = [];
  if (Array.isArray(keywords)) {
    for (const k of keywords) {
      if (typeof k !== 'string') continue;
      const n = k.trim().toLowerCase();
      if (n !== '' && !kw.includes(n) && kw.length < MAX_KEYWORDS) kw.push(n);
    }
  } else {
    kw.push(id);
  }
  const skin: { -readonly [K in keyof ManifestSkin]: ManifestSkin[K] } = {
    id,
    name:
      typeof name === 'string' && name.trim() !== ''
        ? name.trim().slice(0, MAX_NAME_CHARS)
        : defaultSkinName(id),
    file,
    hash: hash.toLowerCase(),
    keywords: Object.freeze(kw),
    order: typeof order === 'number' && Number.isFinite(order) ? order : 0,
  };
  const b = normalizeHexColor(border);
  if (b) skin.border = b;
  if (hidden === true) skin.hidden = true;
  return Object.freeze(skin);
}

function parseColorList(raw: unknown, min: number, max: number): string[] | null {
  if (!Array.isArray(raw)) return null;
  const out: string[] = [];
  for (const c of raw) {
    const n = normalizeHexColor(c);
    if (n) out.push(n);
  }
  return out.length >= min && out.length <= max ? out : null;
}

function parseTheme(raw: unknown): ManifestTheme | null {
  if (!isObject(raw)) return null;
  const theme: { -readonly [K in keyof ManifestTheme]: ManifestTheme[K] } = {};
  const palette = parseColorList(raw['palette'], THEME_PALETTE_MIN, THEME_PALETTE_MAX);
  if (palette) theme.palette = Object.freeze(palette);
  const teams = parseColorList(raw['teams'], 1, 8);
  if (teams) theme.teams = Object.freeze(teams);
  for (const key of [
    'virus',
    'spawner',
    'plain',
    'ink',
    'paperLight',
    'paperDark',
    'gridLight',
    'gridDark',
  ] as const) {
    const c = normalizeHexColor(raw[key]);
    if (c) theme[key] = c;
  }
  return Object.keys(theme).length > 0 ? Object.freeze(theme) : null;
}

/** Validates untrusted JSON into a manifest, dropping bad entries; anything unusable → EMPTY_MANIFEST. */
export function parseManifest(raw: unknown): AssetManifest {
  if (!isObject(raw) || raw['version'] !== 1) return EMPTY_MANIFEST;
  const files: Record<string, ManifestFile> = {};
  let fileCount = 0;
  if (isObject(raw['files'])) {
    for (const [path, entry] of Object.entries(raw['files'])) {
      if (fileCount >= MAX_FILES) break;
      if (!MANIFEST_FILE_RE.test(path)) continue;
      const f = parseFile(entry);
      if (!f) continue;
      files[path] = f;
      fileCount++;
    }
  }
  const skins: ManifestSkin[] = [];
  const seen = new Set<string>();
  if (Array.isArray(raw['skins'])) {
    for (const entry of raw['skins']) {
      if (skins.length >= MAX_SKINS) break;
      const s = parseSkin(entry);
      if (!s || seen.has(s.id)) continue;
      seen.add(s.id);
      skins.push(s);
    }
  }
  const theme = parseTheme(raw['theme']);
  const manifest: { -readonly [K in keyof AssetManifest]: AssetManifest[K] } = {
    version: 1,
    files: Object.freeze(files),
    skins: Object.freeze(skins),
  };
  if (theme) manifest.theme = theme;
  return Object.freeze(manifest);
}

export interface FetchManifestOptions {
  /** Server base for /assets (net/server-url.ts); `""` (default) = this page. */
  readonly baseUrl?: string;
  readonly fetchImpl?: typeof fetch;
  /** Warning sink (default console.warn). */
  readonly warn?: (msg: string) => void;
}

/** The manifest URL for a base origin. */
export function manifestUrl(baseUrl = ''): string {
  return serverUrl(baseUrl, '/assets/manifest.json');
}

function defaultWarn(msg: string): void {
  console.warn(msg);
}

/**
 * GET /assets/manifest.json, or null on any failure (network, HTTP status, JSON). For dev
 * polling, where a transient failure must not wipe the art that is already loaded.
 */
export async function tryFetchManifest(
  opts: FetchManifestOptions = {},
): Promise<{ manifest: AssetManifest } | { error: string }> {
  const fetchImpl = opts.fetchImpl ?? (typeof fetch === 'function' ? fetch : null);
  if (!fetchImpl) return { error: 'fetch is unavailable' };
  try {
    const res = await fetchImpl(manifestUrl(opts.baseUrl), { cache: 'no-cache' });
    if (!res.ok) return { error: `HTTP ${res.status}` };
    const json: unknown = await res.json();
    return { manifest: parseManifest(json) };
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) };
  }
}

/** GET /assets/manifest.json (Cache-Control no-cache). Never rejects: errors → EMPTY_MANIFEST (one console.warn). */
export async function fetchManifest(opts?: FetchManifestOptions): Promise<AssetManifest> {
  const result = await tryFetchManifest(opts);
  if ('manifest' in result) return result.manifest;
  (opts?.warn ?? defaultWarn)(
    `ball.io: could not load /assets/manifest.json (${result.error}); using placeholder art`,
  );
  return EMPTY_MANIFEST;
}

/**
 * Name skin (ui-flow §4.2): the skin whose `keywords` contain the trimmed, lower-cased nick
 * (hidden skins included; lowest `order`, then id, wins), or `""`.
 */
export function nameSkinFor(manifest: AssetManifest, nick: string): string {
  const key = nick.trim().toLowerCase();
  if (key === '') return '';
  let best: ManifestSkin | null = null;
  for (const s of manifest.skins) {
    if (!s.keywords.includes(key)) continue;
    if (!best || s.order < best.order || (s.order === best.order && s.id < best.id)) best = s;
  }
  return best ? best.id : '';
}

/** The manifest entry for a slot path, accepting a `.webp` sibling (art-assets §1: PNG wins). */
export function manifestFileFor(
  manifest: AssetManifest,
  path: string,
): { path: string; file: ManifestFile } | null {
  const png = manifest.files[path];
  if (png) return { path, file: png };
  if (path.endsWith('.png')) {
    const webpPath = `${path.slice(0, -4)}.webp`;
    const webp = manifest.files[webpPath];
    if (webp) return { path: webpPath, file: webp };
  }
  return null;
}

/** Preset skins in picker order: visible ones by `order`, then name (ui-flow §4.6). */
export function pickerSkins(manifest: AssetManifest): ManifestSkin[] {
  return manifest.skins
    .filter((s) => !s.hidden)
    .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name) || (a.id < b.id ? -1 : 1));
}
